// frontend/app/components/lessonPlans/blocks/LessonLoopShell.js
//
// Shared pieces of the five Saras Learning Loop blocks — the AI feedback panel,
// the folded-away rubric, the concept tag and the text-field styling they all
// use. It sits beside LessonBlockShell rather than inside it: these are Loop
// vocabulary, not chrome every block needs.

import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { Pill } from "./LessonBlockShell";

/** The key the reflection's confusion field is written under, per the Loop
 *  block contract. Underscored so it can never collide with a prompt key. */
export const CONFUSION_KEY = "_confusion";

export const countWords = (text) =>
  String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

export const ConceptTag = ({ block, colors, conceptLabels }) => {
  const label = conceptLabels?.[block?.concept_key];
  if (!label) return null;
  return (
    <View style={loopStyles.conceptRow}>
      <Pill colors={colors} label={label} icon="tag-outline" accent={colors.textMuted} />
    </View>
  );
};

/**
 * The evaluator's reply. `source === "heuristic"` means the deterministic
 * fallback scored it — an offline dev box, a missing key or a model timeout.
 * That is still useful feedback, but it is not Saras AI and must not claim to
 * be, so it gets the plain surface and the honest label.
 */
export const AiFeedbackPanel = ({ colors, feedback, t }) => {
  const translate = t || ((value) => value);
  if (!feedback || typeof feedback !== "object") return null;

  const heuristic = feedback.source === "heuristic";
  const strengths = Array.isArray(feedback.strengths) ? feedback.strengths : [];
  const misconceptions = Array.isArray(feedback.misconceptions)
    ? feedback.misconceptions
    : [];

  return (
    <View
      style={[
        loopStyles.aiPanel,
        {
          backgroundColor: heuristic ? colors.subtleBg : colors.aiCardBg,
          borderColor: heuristic ? colors.border : colors.aiCardBorder,
        },
      ]}
    >
      <View style={loopStyles.aiHeader}>
        <MaterialCommunityIcons
          name={heuristic ? "auto-fix" : "robot-happy-outline"}
          size={16}
          color={heuristic ? colors.textSecondary : colors.accentPrimary}
        />
        <Text
          style={[
            loopStyles.aiTitle,
            { color: heuristic ? colors.textSecondary : colors.accentPrimary },
          ]}
        >
          {heuristic ? translate("Automatic check") : translate("Saras AI feedback")}
        </Text>
      </View>

      {feedback.feedback ? (
        <Text style={[loopStyles.aiText, { color: colors.textPrimary }]}>
          {feedback.feedback}
        </Text>
      ) : null}

      {strengths.map((item, index) => (
        <View key={`strength-${index}`} style={loopStyles.aiPointRow}>
          <MaterialCommunityIcons name="check" size={14} color={colors.success} />
          <Text style={[loopStyles.aiPointText, { color: colors.textSecondary }]}>
            {item}
          </Text>
        </View>
      ))}

      {misconceptions.map((item, index) => (
        <View key={`misconception-${index}`} style={loopStyles.aiPointRow}>
          <MaterialCommunityIcons
            name="alert-circle-outline"
            size={14}
            color={colors.warning}
          />
          <Text style={[loopStyles.aiPointText, { color: colors.textSecondary }]}>
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
};

/** A row that folds its own body away. Used for the rubric and the probe hint —
 *  both are guides, and an open one invites copying it back as the answer. */
export const Disclosure = ({ colors, accent, label, onOpen, children }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    setOpen((current) => {
      // Only the closed → open edge reports itself; re-hiding is not a request.
      if (!current && onOpen) onOpen();
      return !current;
    });
  };

  return (
    <View>
      <TouchableOpacity
        onPress={toggle}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={label}
        style={loopStyles.disclosureRow}
      >
        <MaterialCommunityIcons
          name={open ? "chevron-down" : "chevron-right"}
          size={16}
          color={accent || colors.textSecondary}
        />
        <Text
          style={[
            loopStyles.disclosureText,
            { color: accent || colors.textSecondary },
          ]}
        >
          {label}
        </Text>
      </TouchableOpacity>
      {open ? children : null}
    </View>
  );
};

/** The authored rubric, folded away until asked for. */
export const RubricDisclosure = ({ block, colors, t }) => {
  const translate = t || ((value) => value);
  const items = Array.isArray(block?.rubric) ? block.rubric : [];
  if (items.length === 0) return null;

  return (
    <View style={loopStyles.rubric}>
      <Disclosure colors={colors} label={translate("What a strong answer includes")}>
        {items.map((item, index) => (
          <View key={`rubric-${index}`} style={loopStyles.rubricRow}>
            <View
              style={[loopStyles.rubricDot, { backgroundColor: colors.textMuted }]}
            />
            <Text style={[loopStyles.rubricText, { color: colors.textSecondary }]}>
              {item}
            </Text>
          </View>
        ))}
      </Disclosure>
    </View>
  );
};

export const loopStyles = StyleSheet.create({
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    lineHeight: 20,
    minHeight: 76,
    textAlignVertical: "top",
  },
  longInput: { minHeight: 120 },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  wordCount: { fontSize: 11, fontWeight: "700", marginRight: 8 },

  field: { marginBottom: 12 },
  fieldLabel: { fontSize: 13, fontWeight: "600", marginBottom: 6 },

  disclosureRow: { flexDirection: "row", alignItems: "center" },
  disclosureText: { fontSize: 12, fontWeight: "700", marginLeft: 4 },

  rubric: { marginTop: 12 },
  rubricRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 6,
    marginLeft: 20,
  },
  rubricDot: { width: 5, height: 5, borderRadius: 3, marginTop: 7, marginRight: 8 },
  rubricText: { fontSize: 13, lineHeight: 20, flex: 1 },

  aiPanel: { marginTop: 12, borderRadius: 10, borderWidth: 1, padding: 12 },
  aiHeader: { flexDirection: "row", alignItems: "center", marginBottom: 6 },
  aiTitle: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginLeft: 6,
  },
  aiText: { fontSize: 13, lineHeight: 20 },
  aiPointRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 6 },
  aiPointText: { fontSize: 12, lineHeight: 18, flex: 1, marginLeft: 6 },

  conceptRow: { flexDirection: "row", marginTop: 10 },
});
